/**
 * GoodGuardian — Biometric Auth Utility
 *
 * Uses a custom in-app Capacitor plugin (CustodyBiometricPlugin) that wraps
 * iOS LocalAuthentication directly. No external npm package required — the
 * Swift source lives at ios/App/App/BiometricPlugin.swift.
 *
 * Storage keys (localStorage):
 *   cg_bio_enabled  — '1' if the user has opted in to biometric login
 *
 * The JWT token stays in cg_token (localStorage) as normal.
 * Biometrics act as an "app lock" — they gate access to the stored token.
 */

import { Capacitor, registerPlugin } from '@capacitor/core'

const BIO_ENABLED_KEY = 'cg_bio_enabled'

// ── Plugin proxy ──────────────────────────────────────────────────────────────
// registerPlugin creates a lightweight proxy that routes JS calls to the
// native Swift plugin (CustodyBiometricPlugin) via the Capacitor bridge.
// On web (non-native), isNativePlatform() returns false so we never call it.
const _nativePlugin = Capacitor.isNativePlatform()
  ? registerPlugin('CustodyBiometricPlugin')
  : null

function getPlugin() {
  return _nativePlugin
}

// ── BiometryType mapping ──────────────────────────────────────────────────────
// Our Swift plugin returns numeric types matching the standard convention:
// 0 = NONE, 1 = Touch ID / fingerprint, 2 = Face ID / face auth
function mapBiometryType(typeInt) {
  if (typeInt === 2) return 'faceId'
  if (typeInt === 1) return 'touchId'
  return 'none'
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns info about what biometry is available on this device.
 * { available: bool, type: 'faceId' | 'touchId' | 'none' }
 */
export async function getBiometryInfo() {
  const plugin = getPlugin()
  if (!plugin) return { available: false, type: 'none' }
  try {
    const result = await plugin.isAvailable()
    if (!result.isAvailable) return { available: false, type: 'none' }
    return { available: true, type: mapBiometryType(result.biometryType) }
  } catch {
    return { available: false, type: 'none' }
  }
}

/**
 * Prompts the biometric UI. Returns true on success, false on cancel/failure.
 */
export async function authenticate(reason = 'Verify your identity to access GoodGuardian') {
  const plugin = getPlugin()
  if (!plugin) return false
  try {
    await plugin.verifyIdentity({
      reason,
      title: 'GoodGuardian',
      subtitle: 'Biometric Authentication',
      description: reason,
      negativeButtonText: 'Use Password Instead',
    })
    return true
  } catch {
    return false
  }
}

/**
 * Returns whether the user has opted in to biometric login on this device.
 */
export function isBiometricEnabled() {
  return localStorage.getItem(BIO_ENABLED_KEY) === '1'
}

/** Enables biometric login. Call after a successful password login + user consent. */
export function enableBiometric() {
  localStorage.setItem(BIO_ENABLED_KEY, '1')
}

/** Disables biometric login (e.g. from Settings or after logout). */
export function disableBiometric() {
  localStorage.removeItem(BIO_ENABLED_KEY)
}

/** Human-readable label: "Face ID", "Touch ID" */
export function biometryLabel(type) {
  if (type === 'faceId')  return 'Face ID'
  if (type === 'touchId') return 'Touch ID'
  return 'Biometrics'
}

/** Emoji icon for the biometry type */
export function biometryIcon(type) {
  if (type === 'faceId')  return '🔒'
  if (type === 'touchId') return '👆'
  return '🔒'
}
